import type { MouseEvent } from 'react';
import { Popover, Avatar, Text } from '@kula-ai/components';
import { CenterSpinner } from '@/components/ats';
import { useGetUserGroup, type UserGroupUser } from '@/api';

const PoolMemberRow = ({ member }: { member: UserGroupUser }) => (
  <div className="flex items-center gap-2">
    <Avatar src={member.avatar_url} name={member.name} size="xs" />
    <Text variant="p" truncate>
      {member.name}
    </Text>
  </div>
);

interface InterviewPoolPopoverProps {
  poolId: number;
}

export const InterviewPoolPopover = ({ poolId }: InterviewPoolPopoverProps) => {
  const { userGroup, isLoading } = useGetUserGroup({ id: poolId });
  const members = userGroup?.users ?? [];

  // The outer trigger lives inside a react-select option row; swallow
  // mousedown/click so the parent Select doesn't treat the hover as a
  // pick. Hover-engagement (open while cursor sits in the panel so the
  // member list is scrollable) is handled by the Popover's built-in
  // `openOn="hover"` mode — its content keeps the panel alive while the
  // cursor is over it, with a 150 ms travel buffer to/from the trigger.
  const stop = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Popover
      openOn="hover"
      side="top"
      align="end"
      sideOffset={8}
      trigger={
        <span
          className="cursor-pointer text-sm font-medium text-primary-500"
          onClick={stop}
          onMouseDown={stop}
        >
          View Pool
        </span>
      }
    >
      <div className="flex w-48 flex-col gap-1 p-1">
        <Text variant="h3" className="mb-1">
          In this pool
        </Text>
        {isLoading ? (
          <CenterSpinner height="100px" />
        ) : (
          <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
            {members.map((member) => (
              <PoolMemberRow member={member} key={member.id} />
            ))}
          </div>
        )}
      </div>
    </Popover>
  );
};
